import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { FiClock } from 'react-icons/fi';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Badge } from '@/components/ui/badge';
import { AvatarWrapper } from '@/components/ui/avatar';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { ACTIVITY_BADGE_CLASSNAME, isActivityStudentEvaluated } from '../activityStatusVariant';

// Tela de "avaliar atividade" do professor playful (rota
// /atividade-complementar/avaliar-atividades/:id) — igual ao tema Default
// (ActivityEvaluationArchiveDefault): busca a atividade e os activity_students
// reais via API. Quem não enviou resposta não tem registro em
// activity_students, então não aparece na lista — igual ao Default, que só
// lista quem já enviou.

export default function ActivityEvaluationListTeacher({ id, uri }) {
  const navigate = useNavigate();
  const client = useClient();
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);
  const [activityStudents, setActivityStudents] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [activityResult, studentsResult] = await Promise.all([client.fetch(['activities', id]), client.fetch(['activities', id, 'activity_students'])]);

      if (activityResult.error) {
        toast.error('Não foi possível carregar a atividade.');
      } else {
        setActivity(activityResult.data);
      }

      if (studentsResult.error) {
        toast.error('Não foi possível carregar os alunos da atividade.');
      } else {
        setActivityStudents(studentsResult.data || []);
      }

      setLoading(false);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      { route: '/atividade-complementar', name: 'Atividade Complementar', isActive: false },
      { route: uri, name: activity?.title || 'Avaliar atividade', isActive: true }
    ]
  };

  if (loading) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <Loader />
      </DashboardContent>
    );
  }

  if (!activity) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <div className="flex min-h-[50vh] items-center justify-center">
          <EmptyState type="data" />
        </div>
      </DashboardContent>
    );
  }

  const classHours = activity['class-hours'];
  const classHoursLabel = classHours < 2 ? `${classHours} hora` : `${classHours} horas`;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <section
        className="relative mb-6 flex flex-col rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
        style={{ animation: 'trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 150ms both' }}
      >
        <h1
          className="mb-0 line-clamp-2 text-xl font-extrabold leading-snug"
          style={{ color: '#222731' }}
          title={activity.title}
        >
          {activity.title}
        </h1>

        <span className="mt-2 flex w-fit items-center gap-1.5 text-sm font-semibold text-neutral-500">
          <FiClock className="h-4 w-4 text-mission" />
          {classHoursLabel}
        </span>
      </section>

      {activityStudents.length === 0 ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <EmptyState type="data" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {activityStudents.map(activityStudent => {
            const evaluated = isActivityStudentEvaluated(activityStudent);

            return (
              <div
                key={activityStudent.id}
                data-testid="evaluation-student-row"
                data-name={activityStudent.user?.name}
                className="flex flex-wrap items-center gap-4 rounded-2xl border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
              >
                <div className="flex min-w-[160px] flex-1 items-center gap-3">
                  <AvatarWrapper
                    size="sm"
                    src={activityStudent.user?.image?.url}
                    text={activityStudent.user?.name}
                  />
                  <span
                    className="text-sm font-extrabold"
                    style={{ color: '#222731' }}
                  >
                    {activityStudent.user?.name}
                  </span>
                </div>

                <Badge
                  variant={evaluated ? 'success-soft' : 'amber-soft'}
                  className={ACTIVITY_BADGE_CLASSNAME}
                >
                  {evaluated ? 'Avaliação realizada' : 'Aguardando avaliação'}
                </Badge>

                <PlayfulCardButton
                  className="h-9 w-auto shrink-0 px-6"
                  onClick={() => navigate(`/atividade-complementar/avaliar/${id}/${activityStudent.id}`)}
                >
                  {evaluated ? 'Ver avaliação' : 'Acessar'}
                </PlayfulCardButton>
              </div>
            );
          })}
        </div>
      )}
    </DashboardContent>
  );
}
